import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  documentId,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore'
import type { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import type { CareReason, CareReasonStatus } from '@/types/models'
import { toDate } from '@/utils/firestore'
import { getPaginatedCollection } from '@/utils/paginatedQuery'

const COLLECTION = 'care_reasons'
const CARE_REASON_STATUSES: CareReasonStatus[] = [
  'active',
  'complete',
  'indefinite',
]

function isCareReasonStatus(value: unknown): value is CareReasonStatus {
  return CARE_REASON_STATUSES.includes(value as CareReasonStatus)
}

function normalizeCareReasonStatus(
  value: unknown,
  completedAt: Date | undefined,
): CareReasonStatus {
  if (isCareReasonStatus(value)) {
    return value
  }
  if (value === 'resolved') return 'complete'
  return completedAt ? 'complete' : 'active'
}

function normalizePreCompleteStatus(value: unknown): CareReasonStatus | undefined {
  if (value === 'active' || value === 'indefinite') {
    return value
  }
  return undefined
}

function applyStatusUpdate(
  update: Record<string, unknown>,
  data: Partial<Omit<CareReason, 'id'>>,
) {
  if (data.status === 'complete') {
    if (!data.completedAt) {
      update.completedAt = Timestamp.fromDate(new Date())
    }
    return
  }

  if (data.status) {
    update.completedAt = deleteField()
    update.preCompleteStatus = deleteField()
  }
}

function applyCompletedAtUpdate(
  update: Record<string, unknown>,
  data: Partial<Omit<CareReason, 'id'>>,
) {
  if (data.status === 'complete') return

  if (data.completedAt === undefined && 'completedAt' in data) {
    update.completedAt = deleteField()
    return
  }

  if (data.completedAt) {
    update.completedAt = Timestamp.fromDate(data.completedAt)
  }
}

function applyDeletedFieldUpdates(
  update: Record<string, unknown>,
  data: Partial<Omit<CareReason, 'id'>>,
) {
  if ('assignedUserId' in data && !data.assignedUserId) {
    update.assignedUserId = deleteField()
  }

  if ('preCompleteStatus' in data && !data.preCompleteStatus) {
    update.preCompleteStatus = deleteField()
  }
}

function docToCareReason(
  d: { id: string },
  data: Record<string, unknown>,
): CareReason {
  const completedAt = toDate(data.completedAt, true)
  return {
    id: d.id,
    title: data.title as string,
    description: data.description as string,
    status: normalizeCareReasonStatus(data.status, completedAt),
    preCompleteStatus: normalizePreCompleteStatus(data.preCompleteStatus),
    churchMemberId: data.churchMemberId as string,
    assignedUserId: (data.assignedUserId as string) || undefined,
    churchId: data.churchId as string,
    dateOfEvent: toDate(data.dateOfEvent),
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
    completedAt,
    isSensitive: (data.isSensitive as boolean) ?? false,
    createdFromTemplateId: data.createdFromTemplateId as string | undefined,
    createdFromTemplateVersion: data.createdFromTemplateVersion as
      | number
      | undefined,
    createdFromTemplateType: data.createdFromTemplateType as
      | CareReason['createdFromTemplateType']
      | undefined,
    batchId: data.batchId as string | undefined,
    batchItemIndex: data.batchItemIndex as number | undefined,
    batchMutationId: data.batchMutationId as string | undefined,
    deletedAt: toDate(data.deletedAt, true),
    deletedBy: data.deletedBy as string | undefined,
    deletedByBatchOperationId: data.deletedByBatchOperationId as
      | string
      | undefined,
  }
}

export async function getCareReasons(churchId: string): Promise<CareReason[]> {
  const q = query(
    collection(db, COLLECTION),
    where('churchId', '==', churchId),
    where('deletedAt', '==', null),
  )
  const snapshot = await getDocs(q)
  return snapshot.docs.map((d) => docToCareReason(d, d.data()))
}

export const CARE_REASONS_PAGE_SIZE = 50

export interface CareReasonsPage {
  careReasons: CareReason[]
  lastDoc: QueryDocumentSnapshot<DocumentData> | null
}

export async function getCareReasonsPaginated(
  churchId: string,
  pageParam: QueryDocumentSnapshot<DocumentData> | null,
): Promise<CareReasonsPage> {
  const { items, lastDoc } = await getPaginatedCollection({
    collectionName: COLLECTION,
    churchId,
    pageParam,
    orderField: 'dateOfEvent',
    orderDirection: 'desc',
    pageSize: CARE_REASONS_PAGE_SIZE,
    mapper: (d) => docToCareReason(d, d.data()),
  })
  return { careReasons: items, lastDoc }
}

export async function getCareReason(id: string): Promise<CareReason> {
  const snap = await getDoc(doc(db, COLLECTION, id))
  if (!snap.exists()) throw new Error('Care reason not found')
  return docToCareReason(snap, snap.data())
}

export async function getCareReasonsByIds(
  ids: string[],
): Promise<CareReason[]> {
  if (ids.length === 0) return []

  const batches: string[][] = []
  for (let i = 0; i < ids.length; i += 30) {
    batches.push(ids.slice(i, i + 30))
  }

  const results: CareReason[] = []
  for (const batch of batches) {
    const q = query(
      collection(db, COLLECTION),
      where(documentId(), 'in', batch),
    )
    const snapshot = await getDocs(q)
    results.push(...snapshot.docs.map((d) => docToCareReason(d, d.data())))
  }
  return results
}

export async function addCareReason(
  data: Omit<CareReason, 'id' | 'createdAt' | 'updatedAt' | 'completedAt'>,
): Promise<string> {
  const completedAt = data.status === 'complete' ? new Date() : undefined
  const careReasonData: Record<string, unknown> = {
    ...data,
    dateOfEvent: Timestamp.fromDate(data.dateOfEvent),
    ...(completedAt ? { completedAt: Timestamp.fromDate(completedAt) } : {}),
    isSensitive: data.isSensitive ?? false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }
  for (const key of Object.keys(careReasonData)) {
    if (careReasonData[key] === undefined) delete careReasonData[key]
  }
  const ref = await addDoc(collection(db, COLLECTION), careReasonData)
  return ref.id
}

function applyDateOfEvent(
  update: Record<string, unknown>,
  dateOfEvent: Date | undefined,
) {
  if (dateOfEvent) update.dateOfEvent = Timestamp.fromDate(dateOfEvent)
}

export async function updateCareReason(
  id: string,
  data: Partial<Omit<CareReason, 'id'>>,
): Promise<void> {
  const update: Record<string, unknown> = {
    ...data,
    updatedAt: serverTimestamp(),
  }

  applyDateOfEvent(update, data.dateOfEvent)
  applyStatusUpdate(update, data)
  applyCompletedAtUpdate(update, data)
  delete update.createdAt
  applyDeletedFieldUpdates(update, data)

  await updateDoc(doc(db, COLLECTION, id), update)
}

export async function deleteCareReason(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, id))
}
